import { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const componentName = errorInfo.componentStack
      ? extractComponentName(errorInfo.componentStack)
      : 'Unknown';

    console.error('[ErrorBoundary]', {
      component: componentName,
      action: 'render',
      message: error.message,
    });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          role="alert"
          className="flex min-h-screen items-center justify-center bg-surface px-4"
        >
          <div className="text-center">
            <div className="mb-4 text-5xl" aria-hidden="true">
              ⚠️
            </div>
            <h1 className="mb-2 text-2xl font-semibold text-content">
              Something went wrong
            </h1>
            <p className="mb-6 text-content-secondary">
              An unexpected error occurred. Please reload the application to continue.
            </p>
            <button
              onClick={this.handleReload}
              className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-content-on-primary transition-colors hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
            >
              Reload Application
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function extractComponentName(componentStack: string): string {
  const match = componentStack.trim().match(/^\s*at\s+(\w+)/);
  return match?.[1] ?? 'Unknown';
}
